import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Volume2, VolumeX, Play, Pause, Maximize2 } from "lucide-react";

// All five reels are vertical 9:16 tips-style content.
const REELS = [
    { src: "/videos/reel-1.mp4", poster: "/videos/poster-1.jpg" },
    { src: "/videos/reel-2.mp4", poster: "/videos/poster-2.jpg" },
    { src: "/videos/reel-3.mp4", poster: "/videos/poster-3.jpg" },
    { src: "/videos/reel-4.mp4", poster: "/videos/poster-4.jpg" },
    { src: "/videos/reel-5.mp4", poster: "/videos/poster-5.jpg" },
];

function VideoTile({ reel, index, aspect }) {
    const ref = useRef(null);
    const [muted, setMuted] = useState(true);
    const [playing, setPlaying] = useState(false);
    const inView = useInView(ref, { amount: 0.4 });

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (inView) {
            el.play().then(() => setPlaying(true)).catch(() => {});
        } else {
            el.pause();
            setPlaying(false);
        }
    }, [inView]);

    const toggleMute = (e) => {
        e.stopPropagation();
        setMuted((m) => !m);
        if (ref.current) ref.current.muted = !muted;
    };

    const togglePlay = () => {
        const el = ref.current;
        if (!el) return;
        if (el.paused) { el.play(); setPlaying(true); }
        else { el.pause(); setPlaying(false); }
    };

    const goFullscreen = (e) => {
        e.stopPropagation();
        const el = ref.current;
        if (el?.requestFullscreen) el.requestFullscreen();
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: index * 0.06, ease: [0.16, 1, 0.3, 1] }}
            className={`relative group bg-[#0d0d0f] border border-white/10 overflow-hidden ${aspect} cursor-pointer`}
            onClick={togglePlay}
            data-testid={`reel-tile-${index}`}
        >
            <video
                ref={ref}
                src={reel.src}
                poster={reel.poster}
                muted={muted}
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
            />

            {/* Subtle vignette so controls stay visible */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />

            {/* Live indicator */}
            <div className="absolute top-3 left-3 flex items-center gap-2 pointer-events-none">
                <div className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/80">Live</span>
            </div>

            {/* Play state icon */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <div className="w-14 h-14 rounded-full bg-brand/90 backdrop-blur flex items-center justify-center">
                    {playing ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-6 h-6 text-white ml-1" />}
                </div>
            </div>

            {/* Bottom controls */}
            <div className="absolute bottom-3 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                    onClick={toggleMute}
                    aria-label="Toggle sound"
                    className="w-9 h-9 flex items-center justify-center bg-black/50 backdrop-blur border border-white/15 text-white hover:bg-brand hover:border-brand transition-colors"
                >
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                <button
                    onClick={goFullscreen}
                    aria-label="Fullscreen"
                    className="w-9 h-9 flex items-center justify-center bg-black/50 backdrop-blur border border-white/15 text-white hover:bg-brand hover:border-brand transition-colors"
                >
                    <Maximize2 className="w-4 h-4" />
                </button>
            </div>
        </motion.div>
    );
}

export default function Reels() {
    return (
        <section id="reels" className="relative py-24 sm:py-32 border-t border-white/10 overflow-hidden">
            <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-brand/10 blur-[180px]" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="md:col-span-8"
                    >
                        <div className="eyebrow mb-4 flex items-center gap-3">
                            <span className="inline-block w-10 h-[2px] bg-brand" /> Work in motion
                        </div>
                        <h2 className="font-display font-medium tracking-[-0.04em] text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02]">
                            Do this.<br />
                            <span className="text-brand">Not that.</span>
                        </h2>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.15 }}
                        className="md:col-span-4 md:text-right"
                    >
                        <div className="text-white/60 text-sm leading-relaxed">
                            Short tips from the field &mdash; what actually works for Dubai real estate agents. Autoplay on scroll, tap for sound.
                        </div>
                    </motion.div>
                </div>

                {/* Vertical 9:16 tips-style clips */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
                    {REELS.map((r, i) => (
                        <VideoTile key={r.src} reel={r} index={i} aspect="aspect-[9/16]" />
                    ))}
                </div>
            </div>
        </section>
    );
}
